import { Observable, of, forkJoin } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { WeatherIndicatorManager } from './weather-indicator.manager';
import {
  IbmMonthWeatherReportService
} from '@6grain/masofa-monolith-api-client/weather-report/api/ibmMonthWeatherReport.service';
import {IbmMonthWeatherReport} from '@6grain/masofa-monolith-api-client/weather-report';
import {FilterOperator} from '@app/utils/filter-operator';


/**
 * Parameters for IBM monthly data loading
 */
export interface IbmMonthlyLoadParams {
  latitude: number;
  longitude: number;
  year: number;
}

/**
 * Parameters for IBM multi-year data loading
 */
export interface IbmMultiYearLoadParams {
  latitude: number;
  longitude: number;
  years: number[];
}



/**
 * Result of IBM multi-year data loading
 */
export interface IbmMultiYearResult {
  yearDataArrays: any[][];
}


/**
 * Manager for IBM weather data operations
 * Handles monthly data loading by coordinates
 */
export class IbmDataManager {
  constructor(
    private ibmMonthWeatherReportService: IbmMonthWeatherReportService,
    private indicatorManager: WeatherIndicatorManager
  ) {}

  /**
   * Load IBM monthly data for a single year by coordinates
   */
  loadMonthlyData(params: IbmMonthlyLoadParams): Observable<IbmMonthWeatherReport[]> {
    return this.ibmMonthWeatherReportService
      .weatherReportsIbmMonthWeatherReportGetByCoordinatesAndDateInRadiusPost(
        params.latitude,
        params.longitude,
        {
          filters: [
            {
              filterField: 'Year',
              filterOperator: FilterOperator.Equals,
              filterValue: params.year.toString()
            }
          ]
        }
      ).pipe(
      map(results => results ?? []),
      catchError(error => {
        console.error(`Error loading IBM monthly data for year ${params.year}:`, error);
        return of([]);
      })
    );
  }

  /**
   * Load IBM monthly data for multiple years
   */
  loadMultiYearData(params: IbmMultiYearLoadParams): Observable<IbmMultiYearResult> {
    const yearRequests = params.years.map(year =>
      this.loadMonthlyData({
        latitude: params.latitude,
        longitude: params.longitude,
        year: year
      })
    );

    return forkJoin(yearRequests).pipe(
      map(yearDataArrays => ({ yearDataArrays }))
    );
  }

  /**
   * Aggregate daily data into monthly averages/sums
   * Groups data by month and calculates:
   * - Average for: temperatureAverage, temperatureMin, temperatureMax, humidity, windSpeed
   * - Sum for: fallout, sumOfActiveTemperatures*, sumOfSolarRadiation, sumOfFallout
   */
  aggregateToMonthly(dailyData: IbmMonthWeatherReport[]): Map<number, any> {
    const monthlyAggregates = new Map<number, {
      count: number;
      temperatureAverageSum: number;
      temperatureMinSum: number;
      temperatureMaxSum: number;
      humiditySum: number;
      windSpeedSum: number;
      falloutTotal: number;
      sumOfActiveTemperaturesBase5: number;
      sumOfActiveTemperaturesBase7: number;
      sumOfActiveTemperaturesBase10: number;
      sumOfActiveTemperaturesBase12: number;
      sumOfActiveTemperaturesBase15: number;
      sumOfSolarRadiation: number;
      sumOfFallout: number;
    }>();

    // Group and sum data by month
    dailyData.forEach((item: any) => {
      const month = item.month;
      if (!month) return;

      if (!monthlyAggregates.has(month)) {
        monthlyAggregates.set(month, {
          count: 0,
          temperatureAverageSum: 0,
          temperatureMinSum: 0,
          temperatureMaxSum: 0,
          humiditySum: 0,
          windSpeedSum: 0,
          falloutTotal: 0,
          sumOfActiveTemperaturesBase5: 0,
          sumOfActiveTemperaturesBase7: 0,
          sumOfActiveTemperaturesBase10: 0,
          sumOfActiveTemperaturesBase12: 0,
          sumOfActiveTemperaturesBase15: 0,
          sumOfSolarRadiation: 0,
          sumOfFallout: 0
        });
      }

      const agg = monthlyAggregates.get(month)!;
      agg.count++;

      // Sum for averaging
      agg.temperatureAverageSum += item.temperatureAverage ?? 0;
      agg.temperatureMinSum += item.temperatureMin ?? 0;
      agg.temperatureMaxSum += item.temperatureMax ?? 0;
      agg.humiditySum += item.humidity ?? 0;
      agg.windSpeedSum += item.windSpeed ?? 0;

      // Sum totals
      agg.falloutTotal += item.fallout ?? 0;
      agg.sumOfActiveTemperaturesBase5 += item.sumOfActiveTemperaturesBase5 ?? 0;
      agg.sumOfActiveTemperaturesBase7 += item.sumOfActiveTemperaturesBase7 ?? 0;
      agg.sumOfActiveTemperaturesBase10 += item.sumOfActiveTemperaturesBase10 ?? 0;
      agg.sumOfActiveTemperaturesBase12 += item.sumOfActiveTemperaturesBase12 ?? 0;
      agg.sumOfActiveTemperaturesBase15 += item.sumOfActiveTemperaturesBase15 ?? 0;
      agg.sumOfSolarRadiation += item.sumOfSolarRadiation ?? 0;
      agg.sumOfFallout += item.sumOfFallout ?? 0;
    });

    // Convert to final monthly data with averages
    const result = new Map<number, any>();
    monthlyAggregates.forEach((agg, month) => {
      result.set(month, {
        month,
        temperatureAverage: agg.count > 0 ? agg.temperatureAverageSum / agg.count : null,
        temperatureMin: agg.count > 0 ? agg.temperatureMinSum / agg.count : null,
        temperatureMax: agg.count > 0 ? agg.temperatureMaxSum / agg.count : null,
        humidity: agg.count > 0 ? agg.humiditySum / agg.count : null,
        windSpeed: agg.count > 0 ? agg.windSpeedSum / agg.count : null,
        fallout: agg.falloutTotal,
        sumOfActiveTemperaturesBase5: agg.sumOfActiveTemperaturesBase5,
        sumOfActiveTemperaturesBase7: agg.sumOfActiveTemperaturesBase7,
        sumOfActiveTemperaturesBase10: agg.sumOfActiveTemperaturesBase10,
        sumOfActiveTemperaturesBase12: agg.sumOfActiveTemperaturesBase12,
        sumOfActiveTemperaturesBase15: agg.sumOfActiveTemperaturesBase15,
        sumOfSolarRadiation: agg.sumOfSolarRadiation,
        sumOfFallout: agg.sumOfFallout,
        daysCount: agg.count
      });
    });

    return result;
  }

  /**
   * Process monthly data into a map by month (legacy - use aggregateToMonthly for daily data)
   */
  processMonthlyDataByMonth(data: IbmMonthWeatherReport[]): Map<number, any> {
    // Use aggregation to handle daily data
    return this.aggregateToMonthly(data);
  }

  /**
   * Get maximum month based on selected year
   */
  getMaxMonth(selectedYear: number): number {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    return selectedYear === currentYear ? currentMonth : 12;
  }

  /**
   * Extract values for months from data map
   */
  extractMonthlyValues(
    dataByMonth: Map<number, any>,
    maxMonth: number,
    indicator: string
  ): (number | null)[] {
    const values: (number | null)[] = [];

    for (let month = 1; month <= maxMonth; month++) {
      const monthData = dataByMonth.get(month);
      if (monthData) {
        values.push(this.indicatorManager.extractValue(monthData, indicator, 'ibm'));
      } else {
        values.push(null);
      }
    }

    return values;
  }
}
